import { GoogleAuthProvider, signInWithPopup, type UserCredential } from 'firebase/auth';
import { auth } from '../firebase';
import { translateCurrent } from '../i18n';
import { withPopupTimeout } from './oauthPopupTimeout';

/**
 * Web counterpart to googleSignIn.ts. There's no native SDK to get a token
 * from in a browser — Firebase's own signInWithPopup handles the whole
 * OAuth round trip (account picker, consent, credential, sign-in) in one
 * call against the SAME Firebase project/GoogleAuthProvider config native
 * uses, resolving to the same Firebase user/Firestore profile either way.
 */
// No-op on web — there's no native SDK to configure; kept only so
// `export { configureGoogleSignIn } from './googleSignIn'` in useAuth.ts
// resolves on both platforms (nothing currently calls it directly).
export function configureGoogleSignIn(): void {}

// `_t` is unused here — kept only so this matches googleSignIn.ts's
// (native) signature, since useAuth.ts calls `performGoogleSignIn(t)` at
// one shared call site regardless of platform. See appleSignIn.web.ts for
// why translateCurrent() is used instead of the passed `t`.
export async function performGoogleSignIn(_t: (key: string) => string): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  try {
    return await withPopupTimeout(signInWithPopup(auth, provider));
  } catch (error) {
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code?: string }).code)
        : '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      const cancelError = new Error(translateCurrent('auth.googleSignInCancelled'));
      (cancelError as Error & { code?: string }).code = 'SIGN_IN_CANCELLED';
      throw cancelError;
    }
    throw error;
  }
}

export async function signOutGoogle(): Promise<void> {
  // no-op on web — there's no separate native Google session to sign out of;
  // firebaseSignOut(auth) in useAuth.ts's signOut() already covers it.
}
